import { AnimatePresence } from "motion/react";
import { easeOutQuad } from "@/core/easing";
import { Header, Loader } from "@components";

import { useEffect } from "react";
import { useRouter } from "next/router";

// Context providers
import { SearchProvider } from "@/providers/SearchContext";
import { PageHighlightProvider } from "@/providers/PageHighlightContext";
import { RefProvider } from "@/providers/RefContext";
import { ScrollProvider } from "@/providers/ScrollContext";
import { HeaderProvider } from "@/providers/HeaderContext";
import { BusinessHoursProvider } from "@/providers/BusinessHoursContext";
import { YoutubeVideoProvider } from "@/providers/YoutubeVideoContext";
import { VideoPrivacyProvider } from "@/providers/VideoPrivacyContext";
import { PageWrapperDataProvider } from "@/providers/PageWrapperContext";
import { usePageLoading } from "@/providers/LoadingContext";

// Utils
import isRockHallTvTemplate from '@/utils/isRhtvTemplate';
import isCoverStoryTemplate from '@/utils/isCoverStoryTemplate';
import ConditionalFooter from "./ConditionalFooter";
import isDigitalExhibitTemplate from "@/utils/isDXTemplate";

const Page = (props: React.PropsWithChildren<PageProps>) => {
  const { children, className = "", pageWrapperData } = props;
  const router = useRouter();

  // Detect page templates
  const seedNode = (props as any).__SEED_NODE__;
  const isTemplateRockHallTv = isRockHallTvTemplate(seedNode, router.query);
  const isTemplateCoverStory = isCoverStoryTemplate(seedNode);
  const isDigitalExhibit = isDigitalExhibitTemplate(seedNode);

  // Disable Header hide-on-scroll for specific templates
  const disableHeaderHideOnScroll = isTemplateRockHallTv;

  // --------------------

  const { isLoading } = usePageLoading();
  const { businessHours, languages, footerSettings, navigationSettings } = pageWrapperData || {};
  const wrapperClass = [
    className,
    isTemplateRockHallTv ? 'rhtv' : '',
    isTemplateCoverStory ? 'coverstory' : ''
  ].filter(Boolean).join(' ');

  // Ensure <body> gets the same template classes as the wrapper div (client-side)
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('rhtv', isTemplateRockHallTv);
    document.body.classList.toggle('coverstory', isTemplateCoverStory);
    document.body.classList.toggle('digital-exhibit', isDigitalExhibit);
    return () => {
      document.body.classList.remove('rhtv');
      document.body.classList.remove('coverstory');
      document.body.classList.remove('digital-exhibit');
    };
  }, [isTemplateRockHallTv, isTemplateCoverStory, isDigitalExhibit]);

  // -------------------- Render (empty)

  if (!pageWrapperData) return <></>;

  // -------------------- Render

  return (
    <div className={wrapperClass} suppressHydrationWarning={true}>
      <AnimatePresence>
        {isLoading && (
          <Loader
            key="loader"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: easeOutQuad }}
          />
        )}
      </AnimatePresence>
      <SearchProvider>
          <PageHighlightProvider>
            <HeaderProvider>
              <RefProvider>
                <PageWrapperDataProvider {...pageWrapperData}>
                  <ScrollProvider>
                    <Header
                      {...navigationSettings}
                      languages={languages}
                      disableHideOnScroll={disableHeaderHideOnScroll}
                    />
                  </ScrollProvider>
                  <BusinessHoursProvider businessHours={businessHours}>
                    <VideoPrivacyProvider>
                      <YoutubeVideoProvider>
                        {children}
                      </YoutubeVideoProvider>
                    </VideoPrivacyProvider>
                  </BusinessHoursProvider>
                  <ConditionalFooter
                    footerSettings={footerSettings}
                    isCoverStoryTemplate={isTemplateCoverStory}
                    isRockHallTv={isTemplateRockHallTv}
                  />
                </PageWrapperDataProvider>
              </RefProvider>
            </HeaderProvider>
          </PageHighlightProvider>
      </SearchProvider>
    </div>
  );
};

export default Page;
